import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export interface ResourceLink {
  id: number;
  project_id?: string;
  initiative_id?: string;
  label: string;
  url: string;
  category: string | null;
  notes: string | null;
  pinned: boolean;
  sort_order: number;
  added_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface LinkInput {
  label: string;
  url: string;
  category?: string | null;
  notes?: string | null;
  pinned?: boolean;
  added_by?: string | null;
}

export interface LinkPatch {
  label?: string;
  url?: string;
  category?: string | null;
  notes?: string | null;
  pinned?: boolean;
  sort_order?: number;
}

/** Categories used for chips/coloring. Free-form on the backend. */
export const LINK_CATEGORIES = [
  { value: "spec", label: "Spec" },
  { value: "spreadsheet", label: "Spreadsheet" },
  { value: "folder", label: "Folder" },
  { value: "design", label: "Design" },
  { value: "vendor", label: "Vendor" },
  { value: "dashboard", label: "Dashboard" },
  { value: "reference", label: "Reference" },
  { value: "other", label: "Other" },
] as const;

export type LinkCategory = (typeof LINK_CATEGORIES)[number]["value"];

const PROJECT_PREFIX = "/project-links";

// ---------------------------------------------------------------------------
// Project links
// ---------------------------------------------------------------------------

export function useProjectLinks(projectId: string | null) {
  return useQuery<ResourceLink[]>({
    queryKey: [PROJECT_PREFIX, projectId],
    queryFn: () =>
      api.get(`${PROJECT_PREFIX}/${projectId}`).then((r) => r.data),
    enabled: !!projectId,
  });
}

export function useAddProjectLink(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LinkInput) =>
      api.post(`${PROJECT_PREFIX}/${projectId}`, payload).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [PROJECT_PREFIX, projectId] }),
  });
}

export function useUpdateProjectLink(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: LinkPatch & { id: number }) =>
      api.patch(`${PROJECT_PREFIX}/id/${id}`, patch).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [PROJECT_PREFIX, projectId] }),
  });
}

export function useDeleteProjectLink(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`${PROJECT_PREFIX}/id/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [PROJECT_PREFIX, projectId] }),
  });
}

/** Hard cap on how many links can be pinned. Enforced in the UI. */
export const PINNED_LINK_CAP = 3;
